import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    PlugZap, ShieldAlert, Lock, RefreshCcw, Info, Link2Off, Settings2,
} from "lucide-react";
import { hasRole } from "@/lib/auth";
import { Link } from "react-router-dom";

/**
 * Settings · Connected Services
 *
 * Architecture-only surface for VAULT Connect. The feature ships DISABLED
 * behind the `vault_connect` flag. UI shows the disabled state, credential
 * policy, and lets users disconnect + view consent-previews once
 * connections exist.
 */

export default function ConnectedServices() {
    const [status, setStatus] = useState(null);
    const [providers, setProviders] = useState([]);
    const [conns, setConns] = useState([]);
    const canAdmin = hasRole("admin");

    const load = async () => {
        try {
            const [s, p, c] = await Promise.all([
                api.get("/vault-connect/status"),
                api.get("/vault-connect/providers"),
                api.get("/vault-connect/connections"),
            ]);
            setStatus(s.data); setProviders(p.data?.providers || []); setConns(c.data?.connections || []);
        } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't load Connect"); }
    };
    useEffect(() => { load(); }, []);

    const disconnect = async (c) => {
        if (!window.confirm(`Disconnect ${c.institution || c.provider_id}? Historical records will be retained.`)) return;
        try {
            const r = await api.post(`/vault-connect/connections/${c.id}/disconnect`);
            toast.success(r.data?.note || "Disconnected");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't disconnect"); }
    };

    return (
        <div className="max-w-5xl mx-auto p-6 md:p-8" data-testid="connected-services">
            <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-amber-800 font-semibold flex items-center gap-1.5">
                        <PlugZap size={12} /> Settings · Connected Services
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                        VAULT Connect.
                    </h1>
                </div>
                <button onClick={load} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-stone-300 text-[13px] font-semibold text-stone-700 hover:border-stone-500"
                        data-testid="connect-refresh">
                    <RefreshCcw size={12} /> Refresh
                </button>
            </div>

            {/* Status banner */}
            {status && (
                <div className={`rounded-2xl border p-5 mb-6 ${status.enabled ? "border-emerald-200 bg-emerald-50/60" : "border-amber-200 bg-amber-50/60"}`}
                     data-testid="connect-status-banner">
                    <div className="flex items-start gap-3">
                        {status.enabled
                            ? <PlugZap size={16} className="text-emerald-700 mt-0.5 shrink-0" />
                            : <ShieldAlert size={16} className="text-amber-700 mt-0.5 shrink-0" />
                        }
                        <div className="flex-1">
                            <div className="text-[13px] font-semibold text-stone-900">
                                VAULT Connect is {status.enabled ? "ENABLED" : "DISABLED"}
                                <span className="ml-2 text-[10.5px] font-mono text-stone-500">flag · {status.flag_key}</span>
                            </div>
                            {status.disabled_reason && (
                                <p className="text-[12.5px] text-amber-900 mt-1">{status.disabled_reason}</p>
                            )}
                            <div className="mt-3 flex items-start gap-2 text-[11.5px] text-stone-700">
                                <Lock size={11} className="mt-0.5 shrink-0" />
                                <span>
                                    <b>Credential policy:</b> VAULT will <b>never</b> request or store {" "}
                                    {status.credential_policy?.never_requested?.join(", ")}. Only approved
                                    consent-based provider APIs are used.
                                </span>
                            </div>
                        </div>
                        {canAdmin && (
                            <Link to="/admin/integrations/market-approval"
                                  className="text-[12px] font-semibold text-stone-700 hover:text-stone-900 inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-stone-300 hover:border-stone-500 shrink-0"
                                  data-testid="connect-open-market-approval">
                                <Settings2 size={11} /> Market approval
                            </Link>
                        )}
                    </div>
                </div>
            )}

            {/* Providers catalogue */}
            <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden mb-6"
                 data-testid="connect-providers">
                <div className="px-4 py-3 border-b border-stone-100">
                    <h2 className="text-lg font-display text-stone-900">Provider catalogue</h2>
                    <p className="text-[11.5px] text-stone-500 mt-0.5">
                        Reference only. Nothing is active until VAULT Connect is enabled per country and reviewed.
                    </p>
                </div>
                <div className="divide-y divide-stone-100">
                    {providers.map(p => (
                        <div key={p.id} className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
                             data-testid={`connect-provider-${p.id}`}>
                            <div>
                                <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold">{p.kind}</div>
                                <div className="text-[13.5px] font-semibold text-stone-900">{p.name}</div>
                                <div className="text-[11px] text-stone-500 mt-0.5">
                                    Data: {(p.data_categories || []).join(", ")} · Regions: {(p.regions || []).join(", ")}
                                </div>
                            </div>
                            <button disabled
                                    className="px-3 py-1.5 rounded-full border border-stone-300 text-[11.5px] font-semibold text-stone-400 bg-stone-50 cursor-not-allowed"
                                    data-testid={`connect-provider-connect-${p.id}`}>
                                {status?.enabled ? "Connect" : "Awaiting approval"}
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            {/* User's connections (empty until flag on) */}
            <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden"
                 data-testid="connect-your-connections">
                <div className="px-4 py-3 border-b border-stone-100">
                    <h2 className="text-lg font-display text-stone-900">Your connections</h2>
                </div>
                {conns.length === 0 ? (
                    <div className="p-8 text-center text-sm text-stone-400">
                        No connected services yet.
                    </div>
                ) : conns.map(c => (
                    <div key={c.id} className="border-b border-stone-100 last:border-0 px-4 py-3 flex items-center gap-3 flex-wrap"
                         data-testid={`connect-conn-${c.id}`}>
                        <div className="flex-1 min-w-0">
                            <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold">{c.provider_id}</div>
                            <div className="text-[13.5px] font-semibold text-stone-900">{c.institution || c.provider_id}</div>
                            <div className="text-[11px] text-stone-500 mt-0.5">
                                Status · <b>{c.status}</b> · Consent {c.consent_date || "—"} → {c.consent_expiry || "—"} ·
                                Last sync {c.last_sync_at || "never"}
                            </div>
                            {c.data_categories?.length > 0 && (
                                <div className="text-[10.5px] text-stone-500">Data: {c.data_categories.join(", ")}</div>
                            )}
                        </div>
                        <button onClick={() => disconnect(c)}
                                className="text-[11.5px] font-semibold text-rose-700 hover:text-rose-900 px-2.5 py-1 rounded border border-rose-200 inline-flex items-center gap-1"
                                data-testid={`connect-disconnect-${c.id}`}>
                            <Link2Off size={11} /> Disconnect
                        </button>
                    </div>
                ))}
                <div className="px-4 py-2 bg-stone-50 border-t border-stone-100 text-[11px] text-stone-500 flex items-start gap-1.5">
                    <Info size={11} className="mt-0.5" />
                    Disconnecting a service does not delete historical VAULT records. Delete them manually if desired.
                </div>
            </div>
        </div>
    );
}
